import React, { useEffect, useState } from 'react';
import { X, Download, Music, Wand2 } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { fetchAsBlob } from '../../services/storageService';
import EditImagePanel from './EditImagePanel';
import { TYPE } from '../../constants/typeScale';

/**
 * Full-screen "player mode" for a generated/uploaded media asset — image, movie, or audio —
 * opened by tapping the media (or its expand icon) in a chat bubble. Backdrop / X / Esc close it;
 * a download button saves the content to the user's local drive. For IMAGES the caller owns, an
 * Edit action opens the describe-a-change editor in place (`editablePath` = the source storage path).
 */
const MediaPlayerLightbox: React.FC<{ url: string; kind: string; name: string; posterUrl?: string | null; lyrics?: string | null; editablePath?: string | null; conversationId?: string | null; onClose: () => void }>
  = ({ url, kind, name, posterUrl, lyrics, editablePath, conversationId, onClose }) => {
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [editing, setEditing] = useState(false);
  const canEdit = kind === 'image' && !!editablePath;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = async () => {
    try {
      const blob = await fetchAsBlob(url);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name || 'download';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { /* best-effort */ }
  };

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" aria-label="Media viewer" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4" onClick={onClose}>
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {canEdit && !editing && (
          <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} aria-label="Edit image" title="Edit this image"
            className="rounded-full bg-black/40 p-2 text-white/80 hover:text-white hover:bg-black/60"><Wand2 className="h-5 w-5" /></button>
        )}
        <button onClick={(e) => { e.stopPropagation(); download(); }} aria-label="Download" title="Download to your device"
          className="rounded-full bg-black/40 p-2 text-white/80 hover:text-white hover:bg-black/60"><Download className="h-5 w-5" /></button>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close" title="Close"
          className="rounded-full bg-black/40 p-2 text-white/80 hover:text-white hover:bg-black/60"><X className="h-5 w-5" /></button>
      </div>
      {editing && editablePath ? (
        <EditImagePanel sourcePath={editablePath} sourceUrl={url} conversationId={conversationId} onClose={() => setEditing(false)} />
      ) : (
      <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-center">
        {kind === 'video' ? (
          <video src={url} controls autoPlay playsInline className="max-w-[92vw] max-h-[88dvh] rounded-xl shadow-2xl bg-black" />
        ) : kind === 'audio' ? (
          // Song player: cover art on top, then the audio controls, then the lyrics (scrollable) when present.
          <div className="flex w-[86vw] max-w-md flex-col items-center gap-4 rounded-2xl bg-secondary-900 border border-secondary-800 p-6">
            {posterUrl
              ? <img src={posterUrl} alt={name} className="aspect-square w-56 max-w-full rounded-xl object-cover shadow-lg" />
              : <div className="flex aspect-square w-56 max-w-full items-center justify-center rounded-xl bg-secondary-800"><Music className="h-16 w-16 text-secondary-600" /></div>}
            <p className={`w-full truncate text-center ${TYPE.body} font-medium text-white/90`}>{name}</p>
            <audio src={url} controls autoPlay className="w-full" />
            {lyrics && lyrics.trim() && (
              <div className="max-h-[38dvh] w-full overflow-y-auto rounded-lg bg-black/30 p-4">
                <p className={`whitespace-pre-wrap text-center ${TYPE.body} leading-relaxed text-white/80`}>{lyrics.trim()}</p>
              </div>
            )}
          </div>
        ) : (
          <img src={url} alt={name} className="max-w-[92vw] max-h-[88dvh] rounded-xl object-contain shadow-2xl" />
        )}
      </div>
      )}
    </div>
  );
};

export default MediaPlayerLightbox;
